<script setup lang="ts">
import { onMounted, ref, type Ref } from "vue";
import { ProfileAdministrationAPI } from "../api";
import ProjectsTable from "../components/projects/ProjectsTable.vue";
import SupportSearchBar from "../components/SupportSearchBar.vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import { Project, Trainer } from "../types/PersologOnlineTypes.ts";

const { t } = useI18n();
const toast = useToast();

const trainerId = ref("");
const projects: Ref<Project[]> = ref([]);
const trainerEmail = ref("");
const loading = ref(false);
const searched = ref(false);
// Several trainers can share an address (sub-accounts). The match is picked explicitly instead of
// silently loading the projects of whichever one the backend happened to return first.
const matchingTrainers: Ref<Trainer[]> = ref([]);
const selectedTrainer = ref<Trainer | null>(null);

const showLoadError = (error: unknown) => {
    console.error("Could not load the projects", error);
    toast.add({
        severity: "error",
        summary: t("messages.error"),
        detail: t("messages.load.error"),
        life: 4000,
    });
};

const loadProjects = async (trainer: string) => {
    loading.value = true;
    try {
        projects.value = await ProfileAdministrationAPI.fetchProjects(trainer);
    } catch (error) {
        projects.value = [];
        showLoadError(error);
    } finally {
        loading.value = false;
        searched.value = true;
    }
};

const fetchProjects = async () => {
    matchingTrainers.value = [];
    selectedTrainer.value = null;
    await loadProjects(trainerId.value);
};

const selectTrainer = async (trainer: Trainer) => {
    selectedTrainer.value = trainer;
    await loadProjects(trainer.id);
};

const fetchProjectsByEmail = async () => {
    projects.value = [];
    matchingTrainers.value = [];
    selectedTrainer.value = null;
    loading.value = true;

    try {
        const trainers = await ProfileAdministrationAPI.fetchTrainers(trainerEmail.value);
        matchingTrainers.value = trainers;
        loading.value = false;

        if (trainers.length === 1) {
            await selectTrainer(trainers[0]);
        }
    } catch (error) {
        loading.value = false;
        showLoadError(error);
    } finally {
        searched.value = true;
    }
};

const trainerLabel = (trainer: Trainer) => `${trainer.firstname} ${trainer.lastname} — ${trainer.email}`;

onMounted(() => {
    const id = useRoute().query.id;
    if (id) {
        trainerId.value = id.toString();
        fetchProjects();
    }
});
</script>

<template>
    <h1 class="page-title">{{ t("projects.title") }}</h1>
    <Tabs value="0">
        <TabList>
            <Tab value="0">{{ t("projects.input.searchById") }}</Tab>
            <Tab value="1">{{ t("projects.input.searchByEmail") }}</Tab>
        </TabList>
        <TabPanels>
            <TabPanel value="0">
                <SupportSearchBar
                    v-model="trainerId"
                    input-id="trainerID"
                    :placeholder="t('projects.input.idPlaceholder')"
                    :hint="t('projects.input.instruction')"
                    :loading="loading"
                    @search="fetchProjects"
                />
            </TabPanel>
            <TabPanel value="1">
                <SupportSearchBar
                    v-model="trainerEmail"
                    input-id="trainerEmail"
                    :placeholder="t('projects.input.emailPlaceholder')"
                    :hint="t('projects.input.instruction')"
                    :loading="loading"
                    @search="fetchProjectsByEmail"
                />
            </TabPanel>
        </TabPanels>
    </Tabs>

    <ProgressSpinner v-if="loading" style="width: 2rem; height: 2rem" stroke-width="8" />

    <template v-else>
        <div v-if="matchingTrainers.length > 1" class="flex flex-col gap-2 mt-4">
            <span>{{ t("projects.multipleTrainers", { count: matchingTrainers.length }) }}</span>
            <Select
                data-cy="trainer-match-select"
                :model-value="selectedTrainer"
                :options="matchingTrainers"
                :option-label="trainerLabel"
                :placeholder="t('trainer.title')"
                class="w-full md:w-96"
                @update:model-value="selectTrainer"
            />
        </div>

        <Message
            v-if="searched && !projects.length && matchingTrainers.length <= 1"
            severity="info"
            :closable="false"
            class="mt-4"
        >
            {{ t("projects.noResults") }}
        </Message>

        <ProjectsTable :projects="projects" />
    </template>
</template>
